import { AxiosError } from 'axios'
import { FC, useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { StatusModal } from 'src/components/StatusModal'
import {
  AI_LISTENING_BRAND_PROFILE_ROUTE,
  AI_LISTENING_BRAND_ROUTE,
} from 'src/constants/routes'
import { useAiListeningLink } from 'src/hooks/useAiListeningDateRange'
import { useCreateAiListeningProfileMutation } from 'src/reactQuery/useCreateAiListeningProfileMutation'
import { AiListeningErrorEnvelope } from 'src/services/aiListeningApi/brandProfile'
import { BrandProfile } from 'src/services/aiListeningApi/brandProfile.types'
import { AddPromptFlow } from '../shared/AddPrompt'
import { AiListeningPage, PageBreadcrumb } from '../shared/Layout'
import { ProfileForm, ProfileFormValues } from '../shared/ProfileForm'

// Answers the user can act on in the form (a name already in use, a rejected
// website) are shown beside it; anything else gets the failure modal.
const fieldErrorMessage = (
  error: AxiosError<AiListeningErrorEnvelope>
): string | undefined => {
  const status = error.response?.status
  return status === 400 || status === 409
    ? error.response?.data?.error
    : undefined
}

export const CreateBrandProfilePage: FC = () => {
  const navigate = useNavigate()
  const link = useAiListeningLink()
  const [created, setCreated] = useState<BrandProfile | null>(null)
  const [isAddingPrompt, setIsAddingPrompt] = useState(false)
  const [isFailureOpen, setIsFailureOpen] = useState(false)
  const [submitError, setSubmitError] = useState<string>()

  const createMutation = useCreateAiListeningProfileMutation({
    onError: (error) => {
      const message = fieldErrorMessage(error)
      if (message) {
        setSubmitError(message)
      } else {
        setIsFailureOpen(true)
      }
    },
    onSuccess: setCreated,
  })

  const closeFailure = useCallback(() => setIsFailureOpen(false), [])

  const openProfile = () => {
    if (!created) return
    navigate(link(AI_LISTENING_BRAND_PROFILE_ROUTE, { profileId: created._id }))
  }

  const handleSubmit = (values: ProfileFormValues) => {
    setSubmitError(undefined)
    createMutation.mutate({
      aiTools: values.aiTools,
      brandName: values.brandName,
      brandNames: values.aliases,
      competitors: values.competitors.map((brandName) => ({ brandName })),
      name: values.name,
      website: values.website,
    })
  }

  return (
    <AiListeningPage>
      <PageBreadcrumb
        backTo={link(AI_LISTENING_BRAND_ROUTE)}
        items={['AI Listening', 'Create New Brand Profile']}
      />
      <ProfileForm
        isSubmitting={createMutation.isPending}
        onCancel={() => navigate(link(AI_LISTENING_BRAND_ROUTE))}
        onSubmit={handleSubmit}
        submitError={submitError}
        subtitle="Add the basic information about the brand you want to track."
        title="Create Your Brand's Profile"
        variant="brand"
      />

      <StatusModal
        doneLabel="Add Prompt"
        hideCountdown
        isOpen={!!created && !isAddingPrompt}
        message={`Your ${created?.brandName ?? ''} Brand Profile is ready. You can add Prompts now or later.`}
        note={
          <>
            <svg fill="none" height="16" viewBox="0 0 16 16" width="16">
              <circle cx="8" cy="8" fill="currentColor" r="7" />
              <path
                d="M8 7v4M8 5v.01"
                stroke="white"
                strokeLinecap="round"
                strokeWidth="1.5"
              />
            </svg>
            Prompts are specific questions asked to AI models. (Not required)
          </>
        }
        onClose={openProfile}
        onSecondaryAction={() => setIsAddingPrompt(true)}
        secondaryLabel="Skip for Now"
        title="Brand Profile Created Successfully!"
        type="success"
      />

      {created && (
        <AddPromptFlow
          isOpen={isAddingPrompt}
          onClose={openProfile}
          onViewProfile={openProfile}
          profile={created}
        />
      )}

      <StatusModal
        isOpen={isFailureOpen}
        message="We couldn't create the brand profile. Check the details, try different AI models, or try again later."
        onClose={closeFailure}
        title="Failed to Generate Results"
        type="error"
      />
    </AiListeningPage>
  )
}
